import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { resolveCourseLabel, searchCourses } from '@/features/lms/courses/select';
import type { Assessment, AssessmentInput } from '../types';

const schema = z
  .object({
    course: z.string().min(1, 'Course is required'),
    title: z.string().trim().min(1, 'Title is required').max(255),
    description: z.string(),
    max_score: z
      .string()
      .refine((v) => !Number.isNaN(Number(v)) && Number(v) > 0, 'Max score must be > 0'),
    passing_score: z
      .string()
      .refine((v) => !Number.isNaN(Number(v)) && Number(v) >= 0, 'Passing score required'),
    is_active: z.boolean(),
  })
  .refine((v) => Number(v.passing_score) <= Number(v.max_score), {
    message: 'Passing score must be ≤ max score.',
    path: ['passing_score'],
  });

type FormValues = z.infer<typeof schema>;
const FIELD_KEYS = ['course', 'title', 'description', 'max_score', 'passing_score', 'is_active'] as const;

interface AssessmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessment?: Assessment;
  onSubmit: (input: AssessmentInput) => Promise<unknown>;
}

function toValues(assessment?: Assessment): FormValues {
  return {
    course: assessment?.course ? String(assessment.course) : '',
    title: assessment?.title ?? '',
    description: assessment?.description ?? '',
    max_score: String(assessment?.max_score ?? 100),
    passing_score: String(assessment?.passing_score ?? 70),
    is_active: assessment?.is_active ?? true,
  };
}

export function AssessmentFormDialog({ open, onOpenChange, assessment, onSubmit }: AssessmentFormDialogProps) {
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toValues(assessment) });

  const { reset } = form;
  useEffect(() => {
    if (open) reset(toValues(assessment));
  }, [open, assessment, reset]);

  async function handleSubmit(values: FormValues) {
    const input: AssessmentInput = {
      course: Number(values.course),
      title: values.title,
      description: values.description,
      max_score: Number(values.max_score),
      passing_score: Number(values.passing_score),
      is_active: values.is_active,
    };
    try {
      await onSubmit(input);
      onOpenChange(false);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{assessment ? 'Edit assessment' : 'New assessment'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormSection title="Assessment">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Final quiz" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="course"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Course</FormLabel>
                    <FormControl>
                      <EntitySelect
                        queryKey="assessment-course-picker"
                        value={field.value}
                        onChange={field.onChange}
                        search={searchCourses}
                        resolveLabel={resolveCourseLabel}
                        placeholder="Select a course"
                        disabled={Boolean(assessment)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection title="Scoring">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="passing_score"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Passing score</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="max_score"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max score</FormLabel>
                      <FormControl>
                        <Input type="number" min="1" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormSection>

            <FormSection title="Schedule">
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-md border p-3">
                    <FormLabel className="m-0">Active</FormLabel>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </FormSection>

            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving…' : assessment ? 'Save changes' : 'Create assessment'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
